import { useEffect, useRef, useState } from "react";
import { Button, CircularProgress, Rating, useTheme } from "@mui/material";
import { CheckIcon, PlusIcon, TrashIcon, XMarkIcon } from "@heroicons/react/20/solid";
import useFocusTrap from "../../hooks/useFocusTrap";
import { Portal } from "../Portals";
import FormDropdown from "../modals/FormDropdown";
import { panelistDisplayName, panelistIdOf } from "../../utils/panelists";
import {
  FEEDBACK_RESULTS,
  deleteInterviewFeedback,
  feedbackErrorRow,
  fetchFeedbackForm,
  fetchInterviewFeedback,
  getErrorCode,
  getErrorMessage,
  logoutIfSessionExpired,
  saveInterviewFeedback,
} from "../../utils/interviewApi";
import { PanelistAvatar, RESULT_STYLES, ResultPill } from "./InterviewCells";
import { AI_MODE } from "../../utils/aiInterviewApi";

const MAX_ROWS = 50;
const MAX_SKILL = 100;
const MAX_COMMENTS = 2000;
const MAX_FEEDBACK = 10000;

const formatUpdated = (value) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const NO_MISSING = { result: false, feedback: false, skills: [], ratings: [] };
const ERROR_COLOR = "#dc2626";

const inputClassName = "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500";

// `onOpenAIReport` opens the AI results, offered when an AI interviewer interview has no panelists
export default function InterviewFeedbackModal({ isOpen, interview, onClose, onFeedbackChange, onOpenAIReport }) {
  const theme = useTheme();
  const modalRef = useFocusTrap(isOpen, onClose);
  const rowKey = useRef(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState(null);
  const [feedbackByPanelist, setFeedbackByPanelist] = useState({});
  const [selectedId, setSelectedId] = useState("");
  // panelist id → unsaved form, so switching tabs doesn't lose edits
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState("");
  const [errorRow, setErrorRow] = useState(null);
  // Required fields left empty on the last Save; each clears as soon as it's filled
  const [missing, setMissing] = useState(NO_MISSING);
  // Server totals for this interview (average_rating, feedback_count, panelist_count, ...)
  const [summary, setSummary] = useState(null);

  const interviewId = interview?.interview_id;

  const newRow = (skill = "", rating = null, comments = "") => {
    rowKey.current += 1;
    return { key: rowKey.current, skill: skill || "", rating: rating ?? null, comments: comments || "" };
  };

  useEffect(() => {
    if (!isOpen || !interviewId) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    setForm(null);
    setFeedbackByPanelist({});
    setSummary(null);
    setDrafts({});
    setSelectedId("");
    setError("");
    setErrorRow(null);
    setMissing(NO_MISSING);
    setConfirmingDelete(false);

    Promise.all([fetchFeedbackForm(interviewId), fetchInterviewFeedback(interviewId)])
      .then(([formData, interviewFeedback]) => {
        if (cancelled) return;
        const byPanelist = {};
        interviewFeedback.feedback.forEach((item) => {
          const id = item.panelist?.panelist_id;
          if (id) byPanelist[id] = item;
        });
        const panelists = (formData.panelists?.length ? formData.panelists : interview.panelists || [])
          .map((person) => ({ id: panelistIdOf(person), name: panelistDisplayName(person) || person.email || "Panelist", email: person.email || "" }))
          .filter((person) => person.id);
        setForm({
          canAdd: formData.can_add_feedback !== false,
          results: formData.results?.length ? formData.results : FEEDBACK_RESULTS,
          panelists,
          defaultRows: formData.default_skill_assessment || [],
          interview: formData.interview || interview,
        });
        setFeedbackByPanelist(byPanelist);
        setSummary(interviewFeedback.summary);
        setSelectedId(panelists[0]?.id || "");
      })
      .catch((err) => {
        if (cancelled || logoutIfSessionExpired(err)) return;
        setLoadError(getErrorMessage(err, "Could not load feedback for this interview."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // interview is read only to seed the panelist fallback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, interviewId]);

  // A panelist with no feedback opens straight into the form
  const selectedHasFeedback = Boolean(feedbackByPanelist[selectedId]);
  useEffect(() => {
    if (!form?.canAdd || !selectedId || selectedHasFeedback || drafts[selectedId]) return;
    const seeded = (form.defaultRows || []).map((row) => newRow(row.skill, row.rating, row.comments));
    setDrafts((current) => ({
      ...current,
      [selectedId]: { result: "", rows: seeded.length ? seeded : [newRow()], feedback: "" },
    }));
    // newRow only bumps a ref counter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, selectedId, selectedHasFeedback, drafts]);

  if (!isOpen) return null;

  const details = form?.interview || interview || {};
  const candidateName = details.candidate?.name || interview?.candidate?.name || "Candidate";
  const subtitle = [details.jd_title || interview?.jd_title, details.round_name || interview?.round_name]
    .filter(Boolean)
    .join(" · ");
  const panelists = form?.panelists || [];
  const selected = panelists.find((person) => person.id === selectedId);
  const existing = feedbackByPanelist[selectedId];
  const draft = drafts[selectedId];
  const isEditing = Boolean(draft);
  const submittedCount = summary?.feedback_count ?? panelists.filter((person) => feedbackByPanelist[person.id]).length;
  const panelistTotal = summary?.panelist_count ?? panelists.length;
  const overallAverage = typeof summary?.average_rating === "number" ? summary.average_rating : null;

  const draftFrom = (item) => ({
    result: item.result || "",
    rows: (item.skill_assessment || []).map((row) => newRow(row.skill, row.rating, row.comments)),
    feedback: item.feedback || "",
  });

  const clearMessages = () => {
    setError("");
    setErrorRow(null);
    setMissing(NO_MISSING);
    setConfirmingDelete(false);
  };

  const selectPanelist = (id) => {
    setSelectedId(id);
    clearMessages();
  };

  const startEditing = () => {
    clearMessages();
    setDrafts((current) => ({ ...current, [selectedId]: draftFrom(existing) }));
  };

  const stopEditing = () => {
    clearMessages();
    setDrafts((current) => {
      const next = { ...current };
      delete next[selectedId];
      return next;
    });
  };

  const updateDraft = (patch) => {
    setDrafts((current) => ({ ...current, [selectedId]: { ...current[selectedId], ...patch } }));
    if (patch.result) setMissing((current) => ({ ...current, result: false }));
    if (patch.feedback?.trim()) setMissing((current) => ({ ...current, feedback: false }));
  };

  const updateRow = (key, patch) => {
    updateDraft({ rows: draft.rows.map((row) => (row.key === key ? { ...row, ...patch } : row)) });
    setMissing((current) => ({
      ...current,
      skills: patch.skill?.trim() ? current.skills.filter((item) => item !== key) : current.skills,
      ratings: patch.rating ? current.ratings.filter((item) => item !== key) : current.ratings,
    }));
  };

  const validate = () => {
    // Empty required fields first: one message, every empty field outlined
    const empty = {
      result: !draft.result,
      feedback: !draft.feedback.trim(),
      skills: draft.rows.filter((row) => !row.skill.trim()).map((row) => row.key),
      ratings: draft.rows.filter((row) => !row.rating).map((row) => row.key),
    };
    if (empty.result || empty.feedback || empty.skills.length || empty.ratings.length) {
      return { message: "Please fill in all required fields.", missing: empty };
    }
    const seen = new Set();
    for (let index = 0; index < draft.rows.length; index += 1) {
      const row = draft.rows[index];
      const skill = row.skill.trim();
      if (skill.length > MAX_SKILL) return { message: `Skill names must be ${MAX_SKILL} characters or less.`, row: index };
      if (seen.has(skill.toLowerCase())) return { message: `"${skill}" is listed twice. Each skill can appear once.`, row: index };
      seen.add(skill.toLowerCase());
      if (row.comments.length > MAX_COMMENTS) return { message: `Comments must be ${MAX_COMMENTS} characters or less.`, row: index };
    }
    if (draft.feedback.length > MAX_FEEDBACK) return { message: `Feedback must be ${MAX_FEEDBACK} characters or less.` };
    return null;
  };

  const publish = (nextByPanelist) => {
    setFeedbackByPanelist(nextByPanelist);
    if (typeof onFeedbackChange === "function") onFeedbackChange(interviewId);
  };

  const handleSave = async () => {
    const problem = validate();
    if (problem) {
      setError(problem.message);
      setErrorRow(problem.row ?? null);
      setMissing(problem.missing || NO_MISSING);
      return;
    }
    setSaving(true);
    setError("");
    setErrorRow(null);
    setMissing(NO_MISSING);
    try {
      const saved = await saveInterviewFeedback(interviewId, selectedId, {
        result: draft.result,
        skillAssessment: draft.rows.map((row) => ({
          skill: row.skill.trim(),
          rating: Number(row.rating),
          comments: row.comments.trim() || null,
        })),
        feedback: draft.feedback.trim(),
      });
      publish({ ...feedbackByPanelist, [selectedId]: saved });
      setDrafts((current) => {
        const next = { ...current };
        delete next[selectedId];
        return next;
      });
      // The Feedback column already shows the new summary, so we're done
      onClose();
    } catch (err) {
      if (logoutIfSessionExpired(err)) return;
      setError(getErrorMessage(err, "Could not save feedback."));
      setErrorRow(feedbackErrorRow(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await deleteInterviewFeedback(interviewId, selectedId);
    } catch (err) {
      if (logoutIfSessionExpired(err)) return;
      // Already gone on the server: treat as deleted
      if (getErrorCode(err) !== "FEEDBACK_NOT_FOUND") {
        setError(getErrorMessage(err, "Could not delete feedback."));
        setDeleting(false);
        return;
      }
    }
    const next = { ...feedbackByPanelist };
    delete next[selectedId];
    publish(next);
    setConfirmingDelete(false);
    setDeleting(false);
    onClose();
  };

  const notAllowedMessage = interview?.status === "cancelled"
    ? "This interview was cancelled, so feedback can't be added."
    : "Feedback can be added once the interview is scheduled.";

  const outlineButtonSx = {
    textTransform: "none",
    color: theme.palette.text.main,
    backgroundColor: theme.palette.primary.main,
    border: `1px solid ${theme.palette.secondary.v4}`,
    "&:hover": { backgroundColor: theme.palette.secondary.v5 },
  };
  const primaryButtonSx = {
    textTransform: "none",
    backgroundColor: theme.palette.info.main,
    color: theme.palette.text.light,
  };
  const labelStyle = { color: theme.palette.text.main };
  const mutedStyle = { color: theme.palette.text.secondary };

  const renderReadView = () => (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <ResultPill result={existing.result} />
        {typeof existing.average_rating === "number" && (
          <span className="inline-flex items-center gap-1 text-sm">
            <Rating value={existing.average_rating} precision={0.5} readOnly size="small" />
            {/* 3.50 → 3.5, 3.75 stays 3.75 */}
            <span className="font-medium">{Number(existing.average_rating.toFixed(2))}</span>
            <span style={mutedStyle}>average</span>
          </span>
        )}
        {existing.updated_at && (
          <span className="text-xs" style={mutedStyle}>Updated {formatUpdated(existing.updated_at)}</span>
        )}
      </div>

      <div className="overflow-hidden rounded-md border" style={{ borderColor: theme.palette.secondary.v4 }}>
        <div
          className="hidden sm:grid grid-cols-[minmax(0,1fr)_160px_minmax(0,1.4fr)] gap-3 px-3 py-2 text-sm"
          style={{ backgroundColor: theme.palette.secondary.v5 }}
        >
          <span>Skill</span>
          <span>Rating</span>
          <span>Comments</span>
        </div>
        {(existing.skill_assessment || []).map((row, index) => (
          <div
            key={`${row.skill}-${index}`}
            className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_160px_minmax(0,1.4fr)] gap-1 sm:gap-3 px-3 py-2.5 border-t first:border-t-0 sm:first:border-t text-sm"
            style={{ borderColor: theme.palette.secondary.v4 }}
          >
            <span className="font-medium break-words">{row.skill}</span>
            <span className="inline-flex items-center gap-1.5">
              <Rating value={row.rating} precision={0.5} readOnly size="small" />
              <span>{row.rating}</span>
            </span>
            <span className="break-words" style={row.comments ? undefined : mutedStyle}>
              {row.comments || "No comments"}
            </span>
          </div>
        ))}
      </div>

      <div>
        <p className="text-sm font-medium mb-1" style={labelStyle}>Feedback</p>
        <p className="text-sm whitespace-pre-line break-words" style={existing.feedback ? undefined : mutedStyle}>
          {existing.feedback || "No written feedback."}
        </p>
      </div>
    </div>
  );

  const renderForm = () => (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
        <label htmlFor="feedback-result" className="w-24 shrink-0 text-sm font-medium" style={labelStyle}>
          Result <span className="text-red-500">*</span>
        </label>
        <div
          className="w-full rounded-md sm:w-60"
          style={missing.result ? { boxShadow: `0 0 0 1px ${ERROR_COLOR}` } : undefined}
        >
          <FormDropdown
            id="feedback-result"
            value={draft.result}
            onChange={(result) => updateDraft({ result })}
            placeholder="Select result"
            options={form.results.map((result) => {
              const style = RESULT_STYLES[result] || RESULT_STYLES.neutral;
              return {
                value: result,
                label: (
                  <span className="inline-flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: style.dot }} aria-hidden="true" />
                    {style.label || result}
                  </span>
                ),
              };
            })}
          />
        </div>
      </div>

      <div>
        <p className="block text-sm font-medium mb-2" style={labelStyle}>
          Skill assessment <span className="text-red-500">*</span>
        </p>
        <div className="flex flex-col gap-3">
          <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_160px_minmax(0,1.3fr)_32px] gap-3 text-xs" style={mutedStyle}>
            <span>Skill</span>
            <span>Rating</span>
            <span>Comments</span>
            <span className="sr-only">Remove</span>
          </div>
          {draft.rows.map((row, index) => {
            const invalid = errorRow === index;
            const skillMissing = missing.skills.includes(row.key);
            const ratingMissing = missing.ratings.includes(row.key);
            const rowStyle = { borderColor: invalid ? ERROR_COLOR : theme.palette.secondary.v4, color: theme.palette.text.main };
            const skillStyle = { ...rowStyle, borderColor: invalid || skillMissing ? ERROR_COLOR : rowStyle.borderColor };
            return (
              <div
                key={row.key}
                className="grid grid-cols-[minmax(0,1fr)_32px] sm:grid-cols-[minmax(0,1fr)_160px_minmax(0,1.3fr)_32px] items-center gap-x-3 gap-y-2"
              >
                <input
                  id={`feedback-skill-${row.key}`}
                  type="text"
                  value={row.skill}
                  onChange={(event) => updateRow(row.key, { skill: event.target.value })}
                  maxLength={MAX_SKILL}
                  placeholder="Skill"
                  aria-label={`Skill ${index + 1}`}
                  aria-required="true"
                  aria-invalid={invalid || skillMissing || undefined}
                  className={inputClassName}
                  style={skillStyle}
                />
                <div className="flex items-center gap-1.5 sm:order-none order-3 col-span-2 sm:col-span-1">
                  <Rating
                    name={`feedback-rating-${row.key}`}
                    value={row.rating}
                    precision={0.5}
                    onChange={(_, value) => updateRow(row.key, { rating: value })}
                    aria-label={`Rating for ${row.skill || `skill ${index + 1}`}`}
                    // index.css borders any label after a focused input; on half stars that
                    // hits the full-star label and wraps the row. Rating shows its own focus.
                    sx={{
                      "& input:focus + label": { border: 0 },
                      ...(ratingMissing && { "& .MuiRating-iconEmpty": { color: ERROR_COLOR } }),
                    }}
                  />
                  <span className="text-sm w-7" style={ratingMissing ? { color: ERROR_COLOR } : mutedStyle}>{row.rating ?? "–"}</span>
                </div>
                <input
                  id={`feedback-comments-${row.key}`}
                  type="text"
                  value={row.comments}
                  onChange={(event) => updateRow(row.key, { comments: event.target.value })}
                  maxLength={MAX_COMMENTS}
                  placeholder="Comments (optional)"
                  aria-label={`Comments for ${row.skill || `skill ${index + 1}`}`}
                  className={`${inputClassName} order-4 col-span-2 sm:order-none sm:col-span-1`}
                  style={rowStyle}
                />
                <button
                  type="button"
                  onClick={() => updateDraft({ rows: draft.rows.filter((item) => item.key !== row.key) })}
                  disabled={draft.rows.length === 1}
                  aria-label={`Remove ${row.skill || `skill ${index + 1}`}`}
                  className="order-2 sm:order-none inline-flex h-8 w-8 items-center justify-center rounded text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            );
          })}
          <div>
            <button
              type="button"
              onClick={() => updateDraft({ rows: [...draft.rows, newRow()] })}
              disabled={draft.rows.length >= MAX_ROWS}
              className="inline-flex items-center gap-1 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
              style={{ color: theme.palette.info.main }}
            >
              <PlusIcon className="h-4 w-4" />
              Add skill
            </button>
          </div>
        </div>
      </div>

      <div>
        <label htmlFor="feedback-notes" className="block text-sm font-medium mb-2" style={labelStyle}>
          Feedback <span className="text-red-500">*</span>
        </label>
        <textarea
          id="feedback-notes"
          aria-required="true"
          value={draft.feedback}
          onChange={(event) => updateDraft({ feedback: event.target.value })}
          maxLength={MAX_FEEDBACK}
          rows={4}
          placeholder="Overall impression, strengths, concerns"
          aria-invalid={missing.feedback || undefined}
          className={inputClassName}
          style={{ borderColor: missing.feedback ? ERROR_COLOR : theme.palette.secondary.v4, color: theme.palette.text.main }}
        />
      </div>
    </div>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center gap-2 py-16 text-sm" style={mutedStyle}>
          <CircularProgress size={18} sx={{ color: "inherit" }} />
          Loading feedback...
        </div>
      );
    }
    if (loadError) {
      return <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">{loadError}</div>;
    }
    if (!form) return null;
    if (!panelists.length) {
      if ((form.interview?.ai_mode ?? interview?.ai_mode) === AI_MODE.AI_INTERVIEWER) {
        return (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <p className="text-sm" style={mutedStyle}>
              The AI interviewer runs this interview with no panelists. Its results are in the AI report.
            </p>
            {onOpenAIReport && (
              <Button
                onClick={onOpenAIReport}
                sx={{ backgroundColor: theme.palette.info.main, color: theme.palette.text.light, textTransform: "none" }}
              >
                View AI report
              </Button>
            )}
          </div>
        );
      }
      return <p className="text-sm py-8 text-center" style={mutedStyle}>Add panelists to this interview to record feedback.</p>;
    }

    return (
      <>
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            <p className="w-24 shrink-0 text-sm font-medium" style={labelStyle}>Panelists</p>
            <div className="flex min-w-0 flex-1 flex-wrap gap-2" role="tablist" aria-label="Panelists">
            {panelists.map((person) => {
              const item = feedbackByPanelist[person.id];
              const active = person.id === selectedId;
              return (
                <button
                  key={person.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => selectPanelist(person.id)}
                  className="inline-flex items-center gap-2 rounded-md border py-1.5 pl-1.5 pr-3 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
                  style={{
                    borderColor: active ? theme.palette.info.main : theme.palette.secondary.v4,
                    backgroundColor: active ? "#EEF3FB" : theme.palette.primary.main,
                  }}
                >
                  <span className="relative inline-flex">
                    <PanelistAvatar name={person.name} size={28} />
                    {item && (
                      <span
                        aria-hidden="true"
                        className="absolute -bottom-0.5 -right-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full"
                        style={{ backgroundColor: "#16A34A", boxShadow: "0 0 0 2px #ffffff" }}
                      >
                        <CheckIcon className="h-2.5 w-2.5 text-white" />
                      </span>
                    )}
                  </span>
                  <span className="text-sm" style={labelStyle}>
                    {person.name}
                    {item && <span className="sr-only">, feedback submitted</span>}
                  </span>
                </button>
              );
            })}
            </div>
          </div>
          <p className="text-xs sm:pl-28" style={mutedStyle}>
            {submittedCount} of {panelistTotal} submitted
            {overallAverage !== null && ` · ${Number(overallAverage.toFixed(2))} average`}
          </p>
        </div>

        {!form.canAdd && (
          <div className="p-3 rounded border text-sm" style={{ backgroundColor: "#FEF3C7", borderColor: "#FCD34D", color: "#92400E" }}>
            {notAllowedMessage}
          </div>
        )}

        {isEditing ? (
          renderForm()
        ) : existing ? (
          renderReadView()
        ) : (
          <div
            className="flex items-center justify-center rounded-md border border-dashed py-10 text-center"
            style={{ borderColor: theme.palette.secondary.v4 }}
          >
            <p className="text-sm" style={mutedStyle}>{selected?.name || "This panelist"} hasn't submitted feedback yet.</p>
          </div>
        )}

      </>
    );
  };

  // Shown in the footer beside the buttons, so it's visible without scrolling
  const footerError = error ? (
    <p role="alert" className="w-full sm:w-auto sm:flex-1 min-w-0 text-sm" style={{ color: "#b91c1c" }}>
      {error}
    </p>
  ) : null;

  const renderFooter = () => {
    if (loading || loadError || !form || !selected) return null;
    if (isEditing) {
      return (
        <>
          {footerError}
          {existing && <Button onClick={stopEditing} disabled={saving} sx={outlineButtonSx}>Cancel</Button>}
          <Button onClick={handleSave} disabled={saving || !form.canAdd} sx={primaryButtonSx}>
            {saving ? "Saving..." : "Save feedback"}
          </Button>
        </>
      );
    }
    if (!existing) return null;
    if (confirmingDelete) {
      return (
        <>
          {footerError || (
            <span className="w-full sm:w-auto sm:flex-1 min-w-0 text-sm" style={labelStyle}>
              Delete {selected.name}'s feedback? This can't be undone.
            </span>
          )}
          <Button onClick={() => setConfirmingDelete(false)} disabled={deleting} sx={outlineButtonSx}>Keep</Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            sx={{ textTransform: "none", backgroundColor: "#b91c1c", color: "#ffffff", "&:hover": { backgroundColor: "#991b1b" } }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </>
      );
    }
    return (
      <>
        {footerError}
        <Button
          onClick={() => {
            clearMessages();
            setConfirmingDelete(true);
          }}
          sx={{
            ...outlineButtonSx,
            color: "#b91c1c",
            borderColor: "#b91c1c",
            "&:hover": { backgroundColor: "#FEF2F2", borderColor: "#b91c1c" },
          }}
        >
          Delete
        </Button>
        {form.canAdd && <Button onClick={startEditing} sx={primaryButtonSx}>Edit feedback</Button>}
      </>
    );
  };

  const footer = renderFooter();

  // On document.body so it sits above other portaled modals, like the candidate's interview list
  return (
    <Portal>
    <div className="fixed inset-0 flex items-start justify-center z-50 bg-black bg-opacity-70">
      <div className="w-full flex justify-center items-start mt-[70px] px-4">
        <div
          ref={modalRef}
          className="modal-content relative w-full max-w-47rem bg-white rounded-lg shadow-xl max-h-[80vh] flex flex-col overflow-hidden"
          role="dialog"
          aria-modal="true"
          aria-labelledby="interview-feedback-title"
        >
          <div
            className="modal-header flex justify-between items-start gap-3 py-4 px-5 border-b"
            style={{ backgroundColor: theme.palette.primary.main, borderColor: theme.palette.secondary.v4 }}
          >
            <div className="min-w-0">
              <h2 id="interview-feedback-title" className="font-semibold">
                Interview feedback
              </h2>
              <p className="text-sm truncate" style={mutedStyle}>
                {candidateName}
                {subtitle && ` · ${subtitle}`}
              </p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close modal"
              className="inline-flex shrink-0 items-center justify-center rounded-full bg-black text-white h-6 w-6 hover:bg-gray-800 transition-colors"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="flex flex-col px-5 sm:px-6 py-6 gap-5 overflow-y-auto">
            {renderBody()}
          </div>

          {footer && (
            <div
              className="w-full py-3 px-5 flex flex-wrap items-center justify-end gap-2 border-t"
              style={{ borderColor: theme.palette.secondary.v4 }}
            >
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
    </Portal>
  );
}
